import React, { useEffect, useState } from 'react';
import { Check, AlertTriangle, Bell, X, ArrowRight } from 'lucide-react';

const TOAST_ICONS = {
  success: Check,
  warning: AlertTriangle,
  info: Bell,
  handoff: ArrowRight,
};

const TOAST_COLORS = {
  success: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/20',
  warning: 'from-amber-500/20 to-amber-500/5 border-amber-500/20',
  info: 'from-indigo-500/20 to-indigo-500/5 border-indigo-500/20',
  handoff: 'from-purple-500/20 to-purple-500/5 border-purple-500/20',
};

const ICON_COLORS = {
  success: 'text-emerald-400',
  warning: 'text-amber-400',
  info: 'text-indigo-400',
  handoff: 'text-purple-400',
};

const Toast = ({ toast, onDismiss }) => {
  const [exiting, setExiting] = useState(false);
  const Icon = TOAST_ICONS[toast.type] || Bell;

  useEffect(() => {
    const timer = setTimeout(() => setExiting(true), 2700);
    const remove = setTimeout(() => onDismiss(toast.id), 3200);
    return () => { clearTimeout(timer); clearTimeout(remove); };
  }, [toast.id, onDismiss]);

  return (
    <div
      className={`flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl border bg-gradient-to-r backdrop-blur-xl shadow-lg max-w-[340px] transition-all duration-300 ${TOAST_COLORS[toast.type] || TOAST_COLORS.info} ${exiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}
      style={{ animation: 'slideInRight 0.3s ease-out' }}
    >
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${toast.type === 'success' ? 'bg-emerald-500/20' : toast.type === 'warning' ? 'bg-amber-500/20' : 'bg-indigo-500/20'}`}>
        <Icon className={`w-3 h-3 ${ICON_COLORS[toast.type] || ICON_COLORS.info}`} />
      </div>
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-[11px] font-semibold text-white mb-0.5">{toast.title}</p>}
        <p className="text-[10px] text-white/70 leading-relaxed">{toast.message}</p>
      </div>
      <button onClick={() => onDismiss(toast.id)} className="p-0.5 rounded hover:bg-white/10 flex-shrink-0">
        <X className="w-3 h-3 text-white/40" />
      </button>
    </div>
  );
};

const ToastSystem = ({ toasts, onDismiss }) => {
  if (!toasts || toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div className="fixed top-16 right-4 z-[100] flex flex-col gap-2" data-testid="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </>
  );
};

export default ToastSystem;
